import { CareerCard } from '../types/careerCard';
import { Temporal } from '@js-temporal/polyfill';

export interface RubricProgress {
  rubric_item_id: string;
  category: string;
  description: string;
  evidence_count: number;
  last_evidence_date: string;
}

export interface RubricCoverageMetric {
  rubricItemId: string;
  category: string;
  description: string;
  evidenceCount: number;
  coveragePercent: number;
  lastEvidenceDate: string | null;
  daysSinceLastEvidence: number | null;
  status: 'strong' | 'adequate' | 'weak' | 'critical';
  trend: 'improving' | 'stable' | 'declining';
}

export interface Recommendation {
  priority: 'high' | 'medium' | 'low';
  category: string;
  message: string;
  actionType: 'add_evidence' | 'update_evidence' | 'review';
  relatedRubricItem?: string;
}

/**
 * Calculate when Review Prep tab should be available
 */
export const calculateReviewPrepActivation = (
  startDate: string,
  endDate: string
): { isActive: boolean; activationDate: Date; daysUntilEnd: number } => {
  const today = Temporal.Now.plainDateISO();
  const start = Temporal.PlainDate.from(startDate);
  const end = Temporal.PlainDate.from(endDate);
  
  // Calculate days until end (negative if already ended)
  let daysUntilEnd: number;
  if (Temporal.PlainDate.compare(end, today) < 0) {
    // Internship has ended - calculate negative days
    daysUntilEnd = -today.since(end, { largestUnit: 'days' }).days;
  } else {
    // Internship is ongoing - calculate positive days
    daysUntilEnd = end.since(today, { largestUnit: 'days' }).days;
  }
  
  // Calculate total internship duration in days
  const totalDays = end.since(start, { largestUnit: 'days' }).days;
  
  // Calculate 10% threshold
  const tenPercentDays = Math.ceil(totalDays * 0.1);
  const tenPercentDate = end.subtract({ days: tenPercentDays });
  
  // Find Monday of that week
  const dayOfWeek = tenPercentDate.dayOfWeek; // 1 = Monday, 7 = Sunday
  const daysToMonday = dayOfWeek === 1 ? 0 : (8 - dayOfWeek) % 7;
  const mondayDate = tenPercentDate.subtract({ days: daysToMonday });
  
  // One week before end date
  const oneWeekBefore = end.subtract({ days: 7 });
  
  // Choose the later date
  const activationTemporal = Temporal.PlainDate.compare(mondayDate, oneWeekBefore) > 0 ? mondayDate : oneWeekBefore;
  
  // Convert back to Date for compatibility
  const activationDate = new Date(activationTemporal.toString());
  
  return {
    isActive: Temporal.PlainDate.compare(today, activationTemporal) >= 0,
    activationDate,
    daysUntilEnd
  };
};

/**
 * Calculate rubric coverage metrics
 */
export const calculateRubricCoverage = (
  rubricProgress: RubricProgress[],
  cards: CareerCard[],
  contentLinks?: Array<{ source_id: string; target_id: string; source_type: string; target_type: string; [key: string]: any }>,
  config?: {
    defaultEvidenceTarget?: number; // Default number of cards for 100% coverage
    categoryTargets?: Record<string, number>; // Override targets by category
    statusThresholds?: {
      strong: { min_cards: number; recent_days: number };
      adequate: { min_cards: number; recent_days: number };
      weak: { min_cards: number; stale_after_days: number };
    };
  }
): RubricCoverageMetric[] => {
  // Default configuration
  const defaultTarget = config?.defaultEvidenceTarget || 3;
  const categoryTargets = config?.categoryTargets || {};
  return rubricProgress.map(item => {
    // Find all cards linked to this rubric item
    let linkedCards: CareerCard[] = [];
    
    if (contentLinks) {
      // Find cards linked to this rubric criterion via content_links
      const linkedCardIds = contentLinks
        .filter(link => 
          link.target_id === item.rubric_item_id && 
          link.target_type === 'rubric_criterion' &&
          link.source_type === 'career_card'
        )
        .map(link => link.source_id);
      
      linkedCards = cards.filter(card => linkedCardIds.includes(card.id));
    } else {
      // No content links provided - no cards can be linked
      linkedCards = [];
    }
    
    // Get the most recent evidence
    const sortedCards = [...linkedCards].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const lastEvidence = sortedCards[0];
    
    // Calculate days since last evidence
    const daysSinceLastEvidence = lastEvidence
      ? Math.floor((Date.now() - new Date(lastEvidence.created_at).getTime()) / (1000 * 60 * 60 * 24))
      : null;
    
    // Determine status based on count and recency
    const status = getEvidenceStatus(linkedCards.length, daysSinceLastEvidence, config?.statusThresholds);
    
    // Calculate trend (simple version - could be enhanced)
    const trend = calculateTrend(linkedCards);
    
    // Determine target for this item (use category override if available)
    const evidenceTarget = categoryTargets[item.category] || defaultTarget;
    
    return {
      rubricItemId: item.rubric_item_id,
      category: item.category,
      description: item.description,
      evidenceCount: linkedCards.length,
      coveragePercent: Math.min(100, (linkedCards.length / evidenceTarget) * 100),
      lastEvidenceDate: lastEvidence?.created_at || null,
      daysSinceLastEvidence,
      status,
      trend
    };
  });
};

/**
 * Determine evidence status based on count and recency
 */
const getEvidenceStatus = (
  count: number, 
  daysSince: number | null,
  statusThresholds?: {
    strong: { min_cards: number; recent_days: number };
    adequate: { min_cards: number; recent_days: number };
    weak: { min_cards: number; stale_after_days: number };
  }
): 'strong' | 'adequate' | 'weak' | 'critical' => {
  // Use provided thresholds or defaults
  const thresholds = statusThresholds || {
    strong: { min_cards: 3, recent_days: 7 },
    adequate: { min_cards: 2, recent_days: 14 },
    weak: { min_cards: 1, stale_after_days: 14 }
  };
  
  if (count === 0) return 'critical';
  
  // Strong: meets minimum cards and recent activity
  if (count >= thresholds.strong.min_cards && (daysSince === null || daysSince <= thresholds.strong.recent_days)) {
    return 'strong';
  }
  
  // Adequate: meets minimum cards OR fewer cards with recent activity
  if (count >= thresholds.adequate.min_cards || 
      (count >= thresholds.weak.min_cards && daysSince !== null && daysSince <= thresholds.adequate.recent_days)) {
    return 'adequate';
  }
  
  // Weak: has minimum cards but stale
  if (count >= thresholds.weak.min_cards && daysSince !== null && daysSince > thresholds.weak.stale_after_days) {
    return 'weak';
  }
  
  return 'critical';
};

/**
 * Calculate trend based on recent activity
 */
const calculateTrend = (cards: CareerCard[]): 'improving' | 'stable' | 'declining' => {
  if (cards.length === 0) return 'stable';
  
  const now = Date.now();
  const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);
  const twoWeeksAgo = now - (14 * 24 * 60 * 60 * 1000);
  
  const recentCards = cards.filter(c => new Date(c.created_at).getTime() > oneWeekAgo).length;
  const previousWeekCards = cards.filter(c => {
    const time = new Date(c.created_at).getTime();
    return time > twoWeeksAgo && time <= oneWeekAgo;
  }).length;
  
  if (recentCards > previousWeekCards) return 'improving';
  if (recentCards < previousWeekCards) return 'declining';
  return 'stable';
};

/**
 * Generate smart recommendations based on coverage and timeline
 */
export const generateRecommendations = (
  coverage: RubricCoverageMetric[],
  daysUntilEnd: number | null,
  timelineMilestones?: {
    schedule_meeting: number;
    final_week: number;
    confirm_meeting: number;
  }
): Recommendation[] => {
  const recommendations: Recommendation[] = [];
  
  // Critical gaps (no evidence)
  coverage
    .filter(item => item.evidenceCount === 0)
    .forEach(item => {
      recommendations.push({
        priority: 'high',
        category: item.category,
        message: `You have no evidence for "${item.description}". Add an example to strengthen your evaluation.`,
        actionType: 'add_evidence',
        relatedRubricItem: item.rubricItemId
      });
    });
  
  // Weak evidence (only 1 card)
  coverage
    .filter(item => item.evidenceCount === 1)
    .forEach(item => {
      recommendations.push({
        priority: 'medium',
        category: item.category,
        message: `Add more examples for "${item.category}" - you only have 1 piece of evidence.`,
        actionType: 'add_evidence',
        relatedRubricItem: item.rubricItemId
      });
    });
  
  // Stale evidence (no updates in 2+ weeks)
  coverage
    .filter(item => item.daysSinceLastEvidence !== null && item.daysSinceLastEvidence > 14)
    .forEach(item => {
      recommendations.push({
        priority: 'medium',
        category: item.category,
        message: `Your ${item.category} evidence is ${item.daysSinceLastEvidence} days old. Add a recent example to show continued growth.`,
        actionType: 'update_evidence',
        relatedRubricItem: item.rubricItemId
      });
    });
  
  // Time-based recommendations
  const milestones = timelineMilestones || {
    schedule_meeting: 14,
    final_week: 7,
    confirm_meeting: 3
  };
  
  if (daysUntilEnd !== null) {
    if (daysUntilEnd <= milestones.schedule_meeting && daysUntilEnd > milestones.final_week) {
      recommendations.push({
        priority: 'high',
        category: 'general',
        message: 'Schedule your final evaluation meeting with your manager.',
        actionType: 'review'
      });
    }
    
    if (daysUntilEnd <= milestones.final_week) {
      recommendations.push({
        priority: 'high',
        category: 'general',
        message: 'Final week! Review your talking points and prepare for your evaluation.',
        actionType: 'review'
      });
    }
    
    if (daysUntilEnd <= milestones.confirm_meeting) {
      recommendations.push({
        priority: 'high',
        category: 'general',
        message: 'Confirm your evaluation meeting time and location.',
        actionType: 'review'
      });
    }
  }
  
  // Sort by priority
  return recommendations.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
};

/**
 * Calculate overall review prep metrics
 */
export const calculateReviewPrepMetrics = (
  coverage: RubricCoverageMetric[],
  cards: CareerCard[],
  daysUntilEnd: number
) => {
  // Calculate cards per week
  const oldestCard = cards.sort((a, b) => 
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  )[0];
  
  const weeksSinceStart = oldestCard
    ? Math.max(1, Math.floor((Date.now() - new Date(oldestCard.created_at).getTime()) / (1000 * 60 * 60 * 24 * 7)))
    : 1;
  
  const cardsPerWeek = cards.length / weeksSinceStart;
  
  // Recent activity level
  const recentCards = cards.filter(c => {
    const daysSince = Math.floor((Date.now() - new Date(c.created_at).getTime()) / (1000 * 60 * 60 * 24));
    return daysSince <= 7;
  }).length;
  
  const recentActivityLevel = 
    recentCards >= 3 ? 'high' :
    recentCards >= 1 ? 'medium' : 'low';
  
  // Overall coverage score
  const overallCoverageScore = coverage.length > 0
    ? Math.round(coverage.reduce((sum, item) => sum + item.coveragePercent, 0) / coverage.length)
    : 0;
  
  // Critical gaps - group by category to avoid repetition
  const criticalGapsByCategory = coverage
    .filter(item => item.status === 'critical')
    .reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = 0;
      }
      acc[item.category]++;
      return acc;
    }, {} as Record<string, number>);
  
  const criticalGaps = Object.entries(criticalGapsByCategory)
    .map(([category, count]) => `${category} (${count} items)`);
  
  return {
    cardsPerWeek: Math.round(cardsPerWeek * 10) / 10,
    recentActivityLevel,
    overallCoverageScore,
    criticalGaps,
    totalCards: cards.length,
    lastActiveDate: cards.length > 0 
      ? cards.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0].created_at
      : null
  };
};